import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { SystemsModuleService } from './systems-module.service';
import {
	deleteModule,
	deleteModuleError,
	deleteModuleSuccess,
	deleteResourceModule,
	deleteResourceModuleError,
	deleteResourceModuleSuccess,
	deleteVersionsModule,
	deleteVersionsModuleFailure,
	deleteVersionsModuleSuccess,
	getAllModule,
	getAllModuleError,
	getAllModuleSuccess,
	getModule,
	getModuleError,
	getModuleSuccess,
	getResourcesModule,
	getResourcesModuleError,
	getResourcesModuleSuccess,
	getResourcesSearchModuleError,
	getResourcesSearchModuleSuccess,
	getSearchModuleError,
	getSearchModuleSuccess,
	getVersionsModule,
	getVersionsModuleFailure,
	getVersionsModuleSuccess,
	postModule,
	postModuleError,
	postModuleSuccess,
	postResourceModule,
	postResourceModuleError,
	postResourceModuleSuccess,
	postVersionsModule,
	postVersionsModuleFailure,
	postVersionsModuleSuccess,
	putVersionsModule,
	putVersionsModuleFailure,
	putVersionsModuleSuccess,
	updateModule,
	updateModuleError,
	updateModuleSuccess,
	updateResourceModule,
	updateResourceModuleError,
	updateResourceModuleSuccess,
} from './systems-module.action';
import { of } from 'rxjs';
import { ToastAlert } from 'projects/access-control/src/app/Infrastructure/hooks/toast.hook';
import { TranslateService } from '@mangosoft/mango-layout';
import { getResourcesSearchModule, getSearchModule } from './systems-module.action';

@Injectable()
export class SystemsModuleEffects {
	dataTranslate: any;
	constructor(
		private actions$: Actions,
		private systemsModuleService: SystemsModuleService,
		private toastAlert: ToastAlert,
		private translateService: TranslateService
	) {
		this.translateService.init('AccessControl').subscribe((data) => {
			this.dataTranslate = data.pages.systemModules.toasts;
		});
	}

	getModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getModule),
			concatMap(({ pageTo }) =>
				this.systemsModuleService.getModule(pageTo).pipe(
					map(({ data, pagination }) => getModuleSuccess({ module: data, pagination })),
					catchError((error) => of(getModuleError({ error })))
				)
			)
		)
	);

	getAllModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getAllModule),
			concatMap(() =>
				this.systemsModuleService.getAllModule().pipe(
					map(({ data }) => getAllModuleSuccess({ module: data })),
					catchError((error) => of(getAllModuleError({ error })))
				)
			)
		)
	);
	getSearchModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getSearchModule),
			concatMap(({ search }) =>
				this.systemsModuleService.getSearchModule(search).pipe(
					map(({ data }) => getSearchModuleSuccess({ module: data })),
					catchError((error) => of(getSearchModuleError({ error })))
				)
			)
		)
	);

	getResource$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getResourcesModule),
			concatMap(({ url }) =>
				this.systemsModuleService.getResource(url).pipe(
					map(({ pagination, data }) =>
						getResourcesModuleSuccess({
							pagination,
							pk: data.module.pk,
							resource: data.resources,
						})
					),
					catchError((error) => of(getResourcesModuleError({ error })))
				)
			)
		)
	);

	getResourceSearch$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getResourcesSearchModule),
			concatMap(({ url, search }) =>
				this.systemsModuleService.getResourceSearch(url, search).pipe(
					map(({ data, pagination }) =>
						getResourcesSearchModuleSuccess({
							pagination,
							pk: data.module.pk,
							resource: data.resources,
						})
					),
					catchError((error) => of(getResourcesSearchModuleError({ error })))
				)
			)
		)
	);

	addResourceModule$ = createEffect(() =>
		this.actions$.pipe(
			ofType(postResourceModule),
			concatMap(({ pk, sk, resource }) =>
				this.systemsModuleService.createResourceModule(pk, sk, resource).pipe(
					map((resource) =>
						postResourceModuleSuccess({
							resource,
							pk: resource.module.pk,
							sk: resource.module.sk,
						})
					),
					catchError((error) => of(postResourceModuleError({ error })))
				)
			)
		)
	);

	addModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(postModule),
			concatMap(({ module }) =>
				this.systemsModuleService.createModule(module).pipe(
					map((module) => postModuleSuccess({ module })),
					catchError((error) => of(postModuleError({ error })))
				)
			)
		)
	);
	updateModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateModule),
			concatMap(({ pk, sk, module }) =>
				this.systemsModuleService.updateModule(pk, sk, module).pipe(
					map((module) => updateModuleSuccess({ module })),
					catchError((error) => of(updateModuleError({ error })))
				)
			)
		)
	);

	updateResourcesModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateResourceModule),
			concatMap(({ pk, sk, resource }) =>
				this.systemsModuleService.updateResourceModule(pk, sk, resource).pipe(
					map((resource) =>
						updateResourceModuleSuccess({
							resource,
							pk: resource.module.pk,
							sk: resource.module.sk,
						})
					),
					catchError((error) => of(updateResourceModuleError({ error })))
				)
			)
		)
	);

	deleteModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteModule),
			concatMap(({ module }) =>
				this.systemsModuleService.deleteModule(module).pipe(
					map((module) => deleteModuleSuccess({ module })),
					catchError((error) => of(deleteModuleError({ error })))
				)
			)
		)
	);

	deleteResourcesModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteResourceModule),
			concatMap(({ ids, module }) =>
				this.systemsModuleService.deleteResourcesModule(ids, module).pipe(
					map(({ data }) =>
						deleteResourceModuleSuccess({
							ids: data.resource,
							sk: data.module.sk,
							pk: data.module.pk,
						})
					),
					catchError((error) => of(deleteResourceModuleError({ error })))
				)
			)
		)
	);

	getVersionModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getVersionsModule),
			concatMap(({ ids, page, search }) =>
				this.systemsModuleService.getVersionModule(ids, page).pipe(
					map(({ data, pagination }) =>
						getVersionsModuleSuccess({
							versions: data.versions,
							ids: data.module,
							pagination,
						})
					),
					catchError((error) => of(getVersionsModuleFailure({ error })))
				)
			)
		)
	);

	postVersionModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(postVersionsModule),
			concatMap(({ ids, version }) =>
				this.systemsModuleService.postVersionModule(version, ids).pipe(
					map((version) => postVersionsModuleSuccess({ version })),
					catchError((error) => of(postVersionsModuleFailure({ error })))
				)
			)
		)
	);

	updateVersionModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(putVersionsModule),
			concatMap(({ ids, version }) =>
				this.systemsModuleService.updateVersionModule(ids, version).pipe(
					map((version) => putVersionsModuleSuccess({ version })),
					catchError((error) => of(putVersionsModuleFailure({ error })))
				)
			)
		)
	);

	deleteVersionModules$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteVersionsModule),
			concatMap(({ ids, module }) =>
				this.systemsModuleService.deleteVersionModule(ids, module).pipe(
					map(({ data }) => deleteVersionsModuleSuccess({ ids: data.versions })),
					catchError((error) => of(deleteVersionsModuleFailure({ error })))
				)
			)
		)
	);

	CreateModuleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(postModuleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastCreateTitle,
						this.dataTranslate.toastCreateDescription
					);
				})
			),
		{ dispatch: false }
	);

	addResourceModuleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(postResourceModuleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastCreateResourceTitle,
						this.dataTranslate.toastCreateResourceDescription
					);
				})
			),
		{ dispatch: false }
	);

	addVersionModuleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(postVersionsModuleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastCreateVersionTitle,
						this.dataTranslate.toastCreateVersionDescription
					);
				})
			),
		{ dispatch: false }
	);

	updateModuleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(updateModuleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastUpdateTitle,
						this.dataTranslate.toastUpdateDescription
					);
				})
			),
		{ dispatch: false }
	);

	updateResourceModuleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(updateResourceModuleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastUpdateResourceTitle,
						this.dataTranslate.toastUpdateResourceDescription
					);
				})
			),
		{ dispatch: false }
	);

	updateVersionModuleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(putVersionsModuleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastUpdateVersionTitle,
						this.dataTranslate.toastUpdateVersionDescription
					);
				})
			),
		{ dispatch: false }
	);

	deleteResourceModuleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(deleteResourceModuleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastDeleteResourceTitle,
						this.dataTranslate.toastDeleteResourceDescription
					);
				})
			),
		{ dispatch: false }
	);

	deleteModuleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(deleteModuleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastDeleteTitle,
						this.dataTranslate.toastDeleteDescription
					);
				})
			),
		{ dispatch: false }
	);

	deleteVersionSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(deleteVersionsModuleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastDeleteVersionTitle,
						this.dataTranslate.toastDeleteVersionDescription
					);
				})
			),
		{ dispatch: false }
	);

	modulesError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					getModuleError,
					getResourcesModuleError,
					updateResourceModuleError,
					updateModuleError,
					postModuleError,
					postResourceModuleError,
					deleteModuleError,
					deleteResourceModuleError,
					getAllModuleError,
					getSearchModuleError,
					getVersionsModuleFailure,
					putVersionsModuleFailure,
					postVersionsModuleFailure,
          deleteVersionsModuleFailure
				),
				tap(({ error }) => {
					const msgError = error.error.errorMessage || 'Internal Error';
					this.toastAlert.toastFailure('Error', msgError);
				})
			),
		{ dispatch: false }
	);
}
